import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatDate } from "../../utils/TimeAndDateConversions";
import AgendaModal from "../Modals/FloorAgendaModal";

import {
  MDBRow,
  MDBCol,
  MDBInputGroup,
  MDBInput,
  MDBBtn,
  MDBIcon,
  MDBSpinner,
} from "mdb-react-ui-kit";
import axios from "axios";

const Agendas = ({ legislativeCalendarDays, committeeData }) => {
  const [showModal, setShowModal] = useState(false);
  const [customRowData, setCustomRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgenda, setSelectedAgenda] = useState(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: "contains" },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!legislativeCalendarDays || legislativeCalendarDays.length === 0) {
          setLoading(false);
          return;
        }

        // Get current date for filtering future Agendas
        const today = new Date();
        const options = { timeZone: "America/Denver" };
        const usDate = new Date(today.toLocaleString("en-US", options));
        usDate.setHours(23, 59, 59, 999);
        let allRows = [];

        // Legilstive day 0 is coming from the API, so we need to filter it out!
        const filteredCalendarDays = legislativeCalendarDays.filter(
          (day) => day.calendarDay !== 0
        );

        for (const day of filteredCalendarDays) {
          let agendas = [];
          try {
            const agendaResponse = await axios.get(
              `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/agendas/findByLegislativeCalendarDayId?legislativeCalendarDayId=${day.id}`
            );

            const filteredAgendas = agendaResponse.data.filter(
              (agenda) => agenda.chamber === committeeData.chamber
            );

            filteredAgendas.forEach((agenda) => {
              agenda.agendaDate = agenda.agendaDate?.split("T")[0];
            });

            const detailedAgendasPromises = filteredAgendas.map((agenda) =>
              axios.get(
                `${process.env.REACT_APP_LAWS_BACKEND_HOST}/committees/v1/agendas/findByChamberConveneDateAndNumber?chamber=${committeeData.chamber}&conveneDate=${agenda.agendaDate}&number=${agenda.agendaNumber}`
              )
            );

            const detailedAgendasResponses = await Promise.all(
              detailedAgendasPromises
            );
            let detailedAgendas = detailedAgendasResponses
              .map((response) => response.data)
              .flat();

            agendas = detailedAgendas.filter(
              (agenda) => agenda.finalIndicator === true
            );

            agendas = agendas.map((agenda) => ({
              ...agenda,
              legislativeCalendarDay: day,
              formattedAgendaDate: agenda.agendaDate,
            }));
          } catch (error) {
            console.error("Error fetching agendas:", error);
          }

          if (agendas.length === 0) {
            const dayDateTime = `${day.calendarDate}T00:00:00`;
            const dayDate = new Date(dayDateTime);
            const etDayDate = new Date(
              dayDate.toLocaleString("en-US", options)
            );
            if (etDayDate <= usDate) {
              allRows.push({
                ...day,
                committeeData: committeeData,
                agendas: [],
                formattedCalendarDate: formatDate(dayDateTime),
                fullDateTime: new Date(dayDateTime), // Add fullDateTime for sorting
                id: `day-${day.id}`,
              });
            }
          } else {
            agendas.forEach((agenda) => {
              allRows.push({
                ...day,
                committeeData: committeeData,
                agendas: [agenda],
                hasAgendaLink: agendas.length > 0,
                formattedCalendarDate: formatDate(
                  agenda.conveneTime || `${day.calendarDate}T00:00:00`
                ),
                conveneTime: agenda.conveneTime,
                agendaNumber: agenda.agendaNumber,
                fullDateTime: new Date(
                  agenda.conveneTime || `${day.calendarDate}T00:00:00`
                ), // New field for sorting
                id: `day-${day.id}-agenda-${agenda.id}`,
              });
            });
          }
        }
        setCustomRowData(allRows);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [legislativeCalendarDays, committeeData]);

  const legislativeDayTemplate = (rowData) => {
    if (!rowData) return null;
    return <>Day {rowData.calendarDay}</>;
  };

  const dateTimeTemplate = (rowData) => {
    if (!rowData || !rowData.formattedCalendarDate) return null;

    if (rowData.conveneTime) {
      const time = new Date(rowData.conveneTime)
        .toLocaleTimeString("en-US", {
          hour: "numeric", // removes the leading zero
          minute: "2-digit",
          hour12: true,
        })
        .replace(/ (\w{2})$/, "$1"); // removes the space before AM/PM
      return `${rowData.formattedCalendarDate} ${time}`;
    }

    return rowData.formattedCalendarDate;
  };

  const agendasTemplate = (rowData) => {
    if (!rowData || !rowData.agendas || rowData.agendas.length === 0)
      return null;

    return (
      <MDBBtn
        color="link"
        rippleColor="light"
        onClick={() => {
          const agenda = rowData.agendas[0];
          setSelectedAgenda(agenda);
          setShowModal(true);
        }}
      >
        <MDBIcon far icon="list-alt" /> Agenda
      </MDBBtn>
    );
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <MDBSpinner role="status">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );
  }

  return (
    <>
      <MDBRow className="g-1">
        <MDBCol size="lg">
          <MDBInputGroup
            className="mb-3"
            noBorder
            textBefore={<MDBIcon fas icon="search" />}
          >
            <MDBInput
              type="text"
              label="Search Meetings"
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
            />
          </MDBInputGroup>
        </MDBCol>
      </MDBRow>
      <DataTable
        value={customRowData}
        dataKey="id"
        showGridlines
        stripedRows
        resizableColumns
        paginator
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        rows={100}
        rowsPerPageOptions={[25, 50, 100]}
        scrollable
        scrollHeight="580px"
        sortField="fullDateTime"
        sortOrder={-1}
        removableSort
        filters={filters}
        globalFilterFields={["calendarDay", "formattedCalendarDate"]}
        loading={loading}
        emptyMessage="No meetings found"
      >
        <Column
          field="calendarDay"
          header="Legislative Day"
          body={legislativeDayTemplate}
          sortable
        />
        <Column
          field="fullDateTime"
          header="Date & Time"
          body={dateTimeTemplate}
          sortable
        />
        <Column
          field="hasAgendaLink"
          header="Agendas"
          body={agendasTemplate}
          sortable
        />
      </DataTable>
      <AgendaModal
        showModal={showModal}
        setShowModal={setShowModal}
        selectedAgenda={selectedAgenda}
        committeeData={committeeData}
      />
    </>
  );
};

export default Agendas;
